import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Location } from '@angular/common';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PrintService } from '../../services/print.service';
import { ReportsService } from '../../services/reports.service';
import { FULL_NAV_LEVEL, userLevelAtLeast } from '../../constants/access-levels';

export interface CustomerCreditCardRow {
	accountNumber: string;
	branchcode: string;
	customerName: string;
	branchName: string;
	cardId: string;
	cardType: string;
	maskedNumber: string;
	expiry: string;
	locationCode: string;
	locationName: string;
	area: string;
	salesman: string;
}

@Component({
	selector: 'app-customer-credit-cards-report',
	templateUrl: './customer-credit-cards-report.component.html',
	styleUrls: ['./customer-credit-cards-report.component.scss']
})
export class CustomerCreditCardsReportComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild(MatSort, { static: false }) sort: MatSort;

	color = 'blue';
	accessDenied = false;
	sending = false;
	metaLoaded = false;
	locations: { loccode: string; locationname: string }[] = [];
	locationRef = new UntypedFormControl('');
	branchRef = new UntypedFormControl('');
	customerInfoRef = new UntypedFormControl('');
	reportCriteria: any = null;
	totals: { cardCount: number; distinctCustomerBranches: number } | null = null;
	resultSub: Subscription;
	metaSub: Subscription;
	userSub: Subscription;

	dataSource = new MatTableDataSource<CustomerCreditCardRow>([]);
	displayedColumns: string[] = [
		'accountNumber',
		'customerName',
		'branchcode',
		'branchName',
		'locationName',
		'cardType',
		'maskedNumber',
		'expiry',
		'salesman'
	];

	filename = 'Customers_Credit_Cards_';

	constructor(
		private globalSearchService: GlobalSearchService,
		private reportsService: ReportsService,
		private printService: PrintService,
		private locationSvc: Location,
		private cdr: ChangeDetectorRef
	) {
		this.color = this.globalSearchService.getColor();
		this.globalSearchService.mesagesallowed.next(false);
		this.globalSearchService.aiDataAvailable$.next(null);
	}

	ngOnInit(): void {
		this.userSub = this.globalSearchService.user.subscribe((user) => {
			this.accessDenied = !userLevelAtLeast(user, FULL_NAV_LEVEL);
			this.cdr.markForCheck();
		});

		this.metaSub = this.reportsService.getCustomerCreditCardsMeta().subscribe({
			next: (res: any) => {
				if (res?.success === false && res?.error === 'Forbidden') {
					this.accessDenied = true;
				} else if (res && res.success !== false) {
					this.locations = res.locations || [];
					if (!this.reportCriteria) {
						this.runReport();
					}
				}
				this.metaLoaded = true;
				this.cdr.markForCheck();
			},
			error: (err) => {
				if (err?.error?.error === 'Forbidden') {
					this.accessDenied = true;
				}
				this.metaLoaded = true;
			}
		});
	}

	ngAfterViewInit(): void {
		this.attachSort();
	}

	ngOnDestroy(): void {
		this.resultSub?.unsubscribe();
		this.metaSub?.unsubscribe();
		this.userSub?.unsubscribe();
	}

	private attachSort(): void {
		if (this.sort) {
			this.dataSource.sort = this.sort;
		}
	}

	back(): void {
		this.locationSvc.back();
	}

	runReport(): void {
		if (this.accessDenied) {
			return;
		}

		const payload = {
			location: this.locationRef.value || '',
			branchcode: (this.branchRef.value || '').trim(),
			customerinfo: (this.customerInfoRef.value || '').trim()
		};

		this.reportCriteria = payload;
		this.sending = true;
		this.totals = null;
		this.dataSource.data = [];

		this.resultSub?.unsubscribe();
		this.resultSub = this.reportsService.getCustomerCreditCards(payload).subscribe({
			next: (r: any) => {
				this.sending = false;
				if (!r?.success) {
					if (r?.error === 'Forbidden') {
						this.accessDenied = true;
					}
					this.totals = null;
					this.dataSource.data = [];
					return;
				}
				this.totals = r.totals;
				this.dataSource.data = r.rows || [];
				this.globalSearchService.aiDataAvailable$.next(r);
				this.cdr.detectChanges();
				this.attachSort();
			},
			error: (err) => {
				this.sending = false;
				if (err?.error?.error === 'Forbidden') {
					this.accessDenied = true;
				}
			}
		});
	}

	getReportSubtitle(): string {
		if (!this.reportCriteria) {
			return '';
		}
		const p: string[] = [];
		if (this.reportCriteria.location) {
			const l = this.locations.find((x) => x.loccode === this.reportCriteria.location);
			if (l) {
				p.push('Location: ' + l.locationname);
			}
		}
		if (this.reportCriteria.branchcode) {
			p.push('Branch: ' + this.reportCriteria.branchcode);
		}
		if (this.reportCriteria.customerinfo) {
			p.push('Customer: ' + this.reportCriteria.customerinfo);
		}
		return p.join(' · ');
	}

	generateExportTableHtml(): string {
		const rows = this.dataSource.data;
		if (!rows.length) {
			return '';
		}
		const esc = (s: string) =>
			String(s ?? '')
				.replace(/&/g, '&amp;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;');

		let html = `<table class="table"><thead><tr>
			<th>Account #</th><th>Customer</th><th>Branch</th><th>Branch name</th><th>Location</th>
			<th>Card type</th><th>Card number</th><th>Expiry</th><th>Salesperson</th>
		</tr></thead><tbody>`;

		for (const line of rows) {
			html += `<tr>
				<td>${esc(line.accountNumber)}</td>
				<td>${esc(line.customerName)}</td>
				<td>${esc(line.branchcode)}</td>
				<td>${esc(line.branchName)}</td>
				<td>${esc(line.locationName || line.locationCode)}</td>
				<td>${esc(line.cardType)}</td>
				<td>${esc(line.maskedNumber)}</td>
				<td>${esc(line.expiry)}</td>
				<td>${esc(line.salesman)}</td>
			</tr>`;
		}

		html += '</tbody>';
		if (this.totals) {
			html += `<tfoot><tr>
				<td colspan="5"><strong>Cards: ${this.totals.cardCount}</strong> · <strong>Customer branches: ${this.totals.distinctCustomerBranches}</strong></td>
				<td colspan="4"></td>
			</tr></tfoot>`;
		}
		html += '</table>';
		return html;
	}

	exportXls(): void {
		if (!this.dataSource.data.length) {
			return;
		}
		this.sending = true;
		const now = new Date();
		const encoded = this.globalSearchService.base64encode(this.generateExportTableHtml());
		const data = {
			content: encoded,
			filename: this.filename + (now.getMonth() + 1) + now.getDate() + now.getFullYear(),
			title: 'Customers with Credit Cards on Account @ ' + now.toLocaleString(),
			subtitle: this.getReportSubtitle()
		};
		this.printService.xls(data).subscribe({
			next: (result: any) => {
				this.sending = false;
				this.globalSearchService.downloadXls(result.content, data.filename);
			},
			error: () => {
				this.sending = false;
			}
		});
	}
}
